/**
 * Theme management — light / dark / system, matching the Echo design language.
 * The persisted value drives the `.dark` class on <html>. An identical init
 * snippet runs inline in index.html before paint to avoid a flash (FOUC).
 */
const STORAGE_KEY = 'echoproxy.theme';

export type ThemeMode = 'light' | 'dark' | 'system';

const media = (): MediaQueryList => window.matchMedia('(prefers-color-scheme: dark)');

export function getThemeMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

/** Resolve a mode to the concrete theme that should be applied right now. */
export function resolveTheme(mode: ThemeMode = getThemeMode()): 'light' | 'dark' {
  if (mode === 'system') return media().matches ? 'dark' : 'light';
  return mode;
}

function apply(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', resolveTheme(mode) === 'dark');
}

export function setThemeMode(mode: ThemeMode): void {
  if (mode === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, mode);
  apply(mode);
}

/** Flip between light and dark (collapsing `system` to its current resolution). */
export function toggleTheme(): 'light' | 'dark' {
  const next = resolveTheme() === 'dark' ? 'light' : 'dark';
  setThemeMode(next);
  return next;
}

/**
 * Re-apply when the OS theme changes while the user is on `system`.
 * Returns an unsubscribe function.
 */
export function watchSystemTheme(): () => void {
  const mq = media();
  const handler = (): void => {
    if (getThemeMode() === 'system') apply('system');
  };
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
